import { useMemo, useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import { Link } from 'react-router-dom'
import {
  getFiscalYearLabel,
  sumDollars,
  type BudgetCode,
  type Transaction,
} from '@budgetmonkey/shared'
import { Badge, Card, EmptyState, ErrorNote, Input, Money } from '@/components/ui'
import { LoadingOrOffline } from '@/lib/loadState'
import { formatDate } from '@/lib/dates'
import { SOURCE_LABELS } from '@/features/transactions/TransactionList'
import { useExternalTransactions } from '@/features/transactions/hooks'
import { humanErrorMessage } from '@/lib/errorMessage'

/**
 * External charges — REQUIREMENTS.md §8.2b.
 *
 * Answers "what did I put on Ms. Smith's account this semester". Grouped by
 * owner name rather than by code, because the question is about a person: one
 * colleague may have more than one code, and Matt thinks in names.
 *
 * Takes no part in budget math. Nothing here touches a balance.
 */

interface OwnerGroup {
  ownerName: string
  codes: BudgetCode[]
  transactions: Transaction[]
  total: number
  mostRecent: string | null
}

export function ExternalChargesScreen() {
  const fiscalYear = useFiscalYear()
  const { data: rows, state, error } = useExternalTransactions(fiscalYear)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const groups = useMemo<OwnerGroup[]>(() => {
    const byOwner = new Map<string, OwnerGroup>()
    for (const { transaction, code } of rows) {
      if (code === undefined) continue
      const ownerName = code.externalOwnerName ?? code.shortLabel
      let group = byOwner.get(ownerName)
      if (group === undefined) {
        group = {
          ownerName,
          codes: [],
          transactions: [],
          total: 0,
          mostRecent: null,
        }
        byOwner.set(ownerName, group)
      }
      group.transactions.push(transaction)
      if (!group.codes.some((c) => c.id === code.id)) group.codes.push(code)
      if (
        group.mostRecent === null ||
        transaction.transactionDate > group.mostRecent
      ) {
        group.mostRecent = transaction.transactionDate
      }
    }

    for (const group of byOwner.values()) {
      group.total = sumDollars(
        group.transactions
          .filter((t) => t.status !== 'void')
          .map((t) => t.amount),
      )
      group.transactions.sort((a, b) =>
        b.transactionDate.localeCompare(a.transactionDate),
      )
    }

    const needle = search.trim().toLowerCase()
    return [...byOwner.values()]
      .filter(
        (g) =>
          needle === '' ||
          g.ownerName.toLowerCase().includes(needle) ||
          g.transactions.some(
            (t) =>
              t.vendor.toLowerCase().includes(needle) ||
              t.description.toLowerCase().includes(needle),
          ),
      )
      .sort((a, b) => a.ownerName.localeCompare(b.ownerName))
  }, [rows, search])

  const grandTotal = sumDollars(groups.map((g) => g.total))

  if (state !== 'ready') {
    return <LoadingOrOffline state={state} what="These charges" />
  }

  if (error != null) {
    return <ErrorNote>{humanErrorMessage(error, "Couldn't load external charges.")}</ErrorNote>
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">External charges</h1>
        <p className="text-sm text-slate-500">{getFiscalYearLabel(fiscalYear)}</p>
      </div>

      <p className="max-w-prose text-slate-600">
        Purchases you made on your P-Card against a colleague's budget code. They
        appear on your reconciliation packet, and count against none of your
        balances.
      </p>

      {rows.length === 0 ? (
        <EmptyState title="No external charges this fiscal year">
          <p>
            When a colleague asks you to put something on your card, choose their
            code on the{' '}
            <Link to="/transactions" className="underline underline-offset-4">
              transaction
            </Link>{' '}
            — or add one from the code picker.
          </p>
        </EmptyState>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-56 flex-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by person, vendor, or description"
                aria-label="Search external charges"
                type="search"
              />
            </div>
            <p className="text-sm text-slate-600">
              {groups.length} {groups.length === 1 ? 'person' : 'people'} ·{' '}
              <Money value={grandTotal} className="font-medium" />
            </p>
          </div>

          {groups.length === 0 ? (
            <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
              Nothing matches that search.
            </p>
          ) : (
            <ul className="space-y-3">
              {groups.map((group) => (
                <li key={group.ownerName}>
                  <OwnerCard
                    group={group}
                    expanded={expanded.has(group.ownerName)}
                    onToggle={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev)
                        if (next.has(group.ownerName)) next.delete(group.ownerName)
                        else next.add(group.ownerName)
                        return next
                      })
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

function OwnerCard({
  group,
  expanded,
  onToggle,
}: {
  group: OwnerGroup
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <Card className="space-y-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
      >
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{group.ownerName}</p>
          <p className="text-xs text-slate-500">
            {group.transactions.length} charge
            {group.transactions.length === 1 ? '' : 's'}
            {group.mostRecent !== null && (
              <> · most recent {formatDate(group.mostRecent)}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Money value={group.total} className="font-semibold" />
          <span aria-hidden="true" className="text-slate-400">
            {expanded ? '▴' : '▾'}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-slate-100 pt-3">
          <ul className="flex flex-wrap gap-2">
            {group.codes.map((code) => (
              <li key={code.id}>
                <Link
                  to={`/codes/${code.id}`}
                  className="font-mono text-xs text-slate-500 underline underline-offset-4 hover:text-slate-900"
                >
                  {code.code}
                </Link>
              </li>
            ))}
          </ul>

          <ul className="divide-y divide-slate-100">
            {group.transactions.map((transaction) => (
              <li
                key={transaction.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
              >
                <span className="font-medium text-slate-900">
                  {transaction.vendor}
                </span>
                <Badge>{SOURCE_LABELS[transaction.source]}</Badge>
                {transaction.status === 'void' && <Badge tone="danger">Void</Badge>}
                <span className="text-xs text-slate-500">
                  {formatDate(transaction.transactionDate)}
                </span>
                {transaction.description !== '' && (
                  <span className="w-full text-sm text-slate-600">
                    {transaction.description}
                  </span>
                )}
                <Money
                  value={transaction.amount}
                  className={`ml-auto ${
                    transaction.status === 'void'
                      ? 'text-slate-400 line-through'
                      : ''
                  }`}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}
